import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { GET_PROJECTS, GET_CATALOGS } from '../graphql/queries';
import { Project, Catalog } from '../types';
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Filter,
  Map,
  Clock
} from 'lucide-react';

type ViewRange = 'year' | 'quarter' | 'monthly' | 'weekly';

function Roadmap() {
  const navigate = useNavigate();
  const [viewRange, setViewRange] = useState<ViewRange>('year');
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [showTodayLine, setShowTodayLine] = useState<boolean>(true);
  const [statusFilter, setStatusFilter] = useState<string>(() => localStorage.getItem('roadmap_status_filter') ?? '');

  const handleStatusChange = (newStatus: string) => {
    setStatusFilter(newStatus);
    localStorage.setItem('roadmap_status_filter', newStatus);
  };

  const { data, loading } = useQuery<{ projects: Project[] }>(GET_PROJECTS);
  const { data: statusesData } = useQuery<{ catalogs: Catalog[] }>(GET_CATALOGS, { variables: { type: 'projectStatus' } });

  const getBarColor = (priority: string = 'Medium') => {
    const colorMap: Record<string, string> = {
      'Urgent': 'linear-gradient(135deg, #f43f5e, #e11d48)',
      'High': 'linear-gradient(135deg, #f97316, #ea580c)',
      'Medium': 'linear-gradient(135deg, #eab308, #d97706)',
      'Low': 'linear-gradient(135deg, #10b981, #059669)'
    };
    return colorMap[priority] || 'linear-gradient(135deg, var(--primary), #818cf8)';
  };

  // Helper date functions
  const startOfYear = (year: number) => new Date(year, 0, 1);
  const endOfYear = (year: number) => new Date(year, 11, 31, 23, 59, 59);

  const startOfMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1);
  const endOfMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59);

  const getMonday = (d: Date) => {
    const date = new Date(d);
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is sunday
    return new Date(date.setDate(diff));
  };
  
  const startOfWeek = (date: Date) => {
    const monday = getMonday(date);
    monday.setHours(0, 0, 0, 0);
    return monday;
  };

  const endOfWeek = (date: Date) => {
    const monday = getMonday(date);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);
    return sunday;
  };

  // Calculate timeline bounds based on range
  let startBound: Date;
  let endBound: Date;
  let ticks: string[] = [];

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  if (viewRange === 'year') {
    startBound = startOfYear(year);
    endBound = endOfYear(year);
    ticks = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  } else if (viewRange === 'quarter') {
    startBound = startOfYear(year);
    endBound = endOfYear(year);
    ticks = ['Q1 (Jan-Mar)', 'Q2 (Apr-Jun)', 'Q3 (Jul-Sep)', 'Q4 (Oct-Dec)'];
  } else if (viewRange === 'monthly') {
    startBound = startOfMonth(currentDate);
    endBound = endOfMonth(currentDate);
    ticks = ['Week 1', 'Week 2', 'Week 3', 'Week 4', 'Week 5'];
  } else { // weekly
    startBound = startOfWeek(currentDate);
    endBound = endOfWeek(currentDate);
    ticks = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  }

  const today = new Date();
  const startMs = startBound.getTime();
  const endMs = endBound.getTime();
  const totalMs = endMs - startMs;
  const todayMs = today.getTime();
  const todayLeft = ((todayMs - startMs) / totalMs) * 100;
  const isTodayInBounds = todayMs >= startMs && todayMs <= endMs;

  const navigatePeriod = (direction: 'prev' | 'next') => {
    const change = direction === 'prev' ? -1 : 1;
    const newDate = new Date(currentDate);

    if (viewRange === 'year' || viewRange === 'quarter') {
      newDate.setFullYear(currentDate.getFullYear() + change);
    } else if (viewRange === 'monthly') {
      newDate.setMonth(currentDate.getMonth() + change);
    } else { // weekly
      newDate.setDate(currentDate.getDate() + change * 7);
    }
    setCurrentDate(newDate);
  };

  const formatPeriodLabel = () => {
    if (viewRange === 'year' || viewRange === 'quarter') {
      return `${year}`;
    } else if (viewRange === 'monthly') {
      return currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    } else { // weekly
      const start = startOfWeek(currentDate);
      const end = endOfWeek(currentDate);
      return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Filter active projects that overlap with the current timeline range
  const filteredProjects = data?.projects?.filter((project: Project) => {
    // Basic status filter
    if (statusFilter && project.status !== statusFilter) return false;

    // Dates check
    if (!project.dueDate) return false;
    const projectStart = project.startDate ? new Date(project.startDate) : project.createdAt ? new Date(project.createdAt) : new Date();
    const projectEnd = new Date(project.dueDate);

    // Overlaps with bound check
    return projectStart <= endBound && projectEnd >= startBound;
  }) || [];

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Map size={24} style={{ color: 'var(--primary)' }} />
            Roadmap
          </h1>
        </div>
        
        {/* View Range Selector */}
        <div className="filter-tabs" style={{ margin: 0 }}>
          {(['year', 'quarter', 'monthly', 'weekly'] as ViewRange[]).map((range) => (
            <button
              key={range}
              className={`filter-tab ${viewRange === range ? 'active' : ''}`}
              onClick={() => {
                setViewRange(range);
                setCurrentDate(new Date()); // Reset to today
              }}
              style={{ textTransform: 'capitalize' }}
            >
              {range === 'monthly' ? 'Month' : range === 'weekly' ? 'Week' : range}
            </button>
          ))}
        </div>
      </div>

      <div className="page-content">
        <div className="roadmap-container">
          
          {/* Timeline Toolbar Controls */}
          <div className="filters-bar" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button className="btn btn-secondary">
                <Filter size={16} />
                Status
              </button>
              
              <select
                className="filter-select"
                value={statusFilter}
                onChange={(e) => handleStatusChange(e.target.value)}
              >
                <option value="">All Statuses</option>
                {statusesData?.catalogs?.map(status => (
                  <option key={status.id} value={status.name}>{status.name}</option>
                ))}
              </select>
              <button 
                type="button"
                className={`btn ${showTodayLine ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setShowTodayLine(!showTodayLine)}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '38px', width: '38px', padding: 0 }}
                title={showTodayLine ? 'Ocultar indicador de Hoy' : 'Mostrar indicador de Hoy'}
                aria-label={showTodayLine ? 'Ocultar indicador de Hoy' : 'Mostrar indicador de Hoy'}
              >
                <Clock size={16} />
              </button>
            </div>

            {/* Pagination Controls */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <button className="btn-icon" onClick={() => navigatePeriod('prev')} aria-label="Periodo anterior">
                <ChevronLeft size={18} />
              </button>
              
              <span style={{ fontSize: '15px', fontWeight: 700, minWidth: '120px', textAlign: 'center', color: 'var(--text-primary)' }}>
                {formatPeriodLabel()}
              </span>

              <button className="btn-icon" onClick={() => navigatePeriod('next')} aria-label="Periodo siguiente">
                <ChevronRight size={18} />
              </button>
            </div>
          </div>

          {/* Timeline Grid Card */}
          <div className="roadmap-timeline-card">
            <div className="roadmap-timeline-wrapper">
              <div 
                className="roadmap-timeline-grid"
                style={{ '--ticks-count': ticks.length } as React.CSSProperties}
              >
                {/* Today Line Indicator */}
                {showTodayLine && isTodayInBounds && (
                  <div className="roadmap-today-line-container">
                    <div 
                      className="roadmap-today-line"
                      style={{ left: `${todayLeft}%` }}
                      title={`Hoy: ${formatDate(today.toISOString())}`}
                    />
                  </div>
                )}

                {/* Header Row: Time Ticks */}
                <div className="roadmap-header-row">
                  <div className="roadmap-project-col-header">
                    Projects
                  </div>
                  <div className="roadmap-timeline-cols-header">
                    {ticks.map((tick, idx) => (
                      <div key={idx} className="roadmap-header-tick">
                        {tick}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Body Rows: Project Bars */}
                <div className="roadmap-body-rows">
                  {filteredProjects.map((project: Project) => {
                    const startMs = startBound.getTime();
                    const endMs = endBound.getTime();
                    const totalMs = endMs - startMs;

                    const projectStart = project.startDate ? new Date(project.startDate) : project.createdAt ? new Date(project.createdAt) : new Date();
                    const projectEnd = new Date(project.dueDate!);

                    // Clamp bounds
                    const clampedStart = Math.max(startMs, projectStart.getTime());
                    const clampedEnd = Math.min(endMs, projectEnd.getTime());

                    // Compute percentages
                    const left = ((clampedStart - startMs) / totalMs) * 100;
                    const width = Math.max(3, ((clampedEnd - clampedStart) / totalMs) * 100); // min 3% width

                    return (
                      <div key={project.id} className="roadmap-project-row">
                        {/* Meta info cell on the left */}
                        <div className="roadmap-project-info-cell">
                          <h4 onClick={() => navigate(`/projects/${project.id}`)}>
                            {project.name}
                          </h4>
                          <p>{project.client?.company || 'No Client'}</p>
                        </div>

                        {/* Visual timeline bar on the right */}
                        <div className="roadmap-timeline-cell">
                          <div
                            className="roadmap-project-bar"
                            onClick={() => navigate(`/projects/${project.id}`)}
                            style={{
                              left: `${left}%`,
                              width: `${width}%`,
                              background: getBarColor(project.priority)
                            }}
                            title={`${project.name}\nClient: ${project.client?.company || 'None'}\nPriority: ${project.priority}\nTimeline: ${formatDate(project.startDate || project.createdAt)} - ${formatDate(project.dueDate)}`}
                          >
                            <div className="roadmap-bar-title">
                              {project.name}
                            </div>
                            <div className="roadmap-bar-meta">
                              <span className="roadmap-bar-priority">
                                {project.priority || 'Medium'}
                              </span>
                              <span>•</span>
                              <span>
                                {project.client?.company || 'No Client'}
                              </span>
                              <span>•</span>
                              <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                <Clock size={10} />
                                {formatDate(project.startDate || project.createdAt)} - {formatDate(project.dueDate)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {filteredProjects.length === 0 && (
                    <div className="empty-state" style={{ border: 'none' }}>
                      <Calendar size={64} />
                      <h3>No active projects</h3>
                      <p>No projects overlap with the selected timeline period.</p>
                    </div>
                  )}
                </div>

              </div>
            </div>
          </div>

        </div>
      </div>
    </>
  );
}

export default Roadmap;
